import React, { Component } from "react";
import PropTypes from "prop-types";
import ReactAudioPlayer from "react-audio-player";
import { withRouter } from "react-router-dom";

import { isCordovaApp, CordovaAudio } from "../../../utils/cordovaUtils";
import { fetchAudioChapter } from "../../../api/BibleApi";

const ChapterAudioContext = React.createContext();

class ChapterAudio extends Component {
  static Consumer = ChapterAudioContext.Consumer;

  static propTypes = {
    chapter: PropTypes.shape({
      id: PropTypes.string.isRequired,
      bibleId: PropTypes.string.isRequired,
      reference: PropTypes.string.isRequired,
      next: PropTypes.shape({
        id: PropTypes.string.isRequired
      }).isRequired
    }),
    bible: PropTypes.shape({
      id: PropTypes.string.isRequired,
      nameLocal: PropTypes.string.isRequired,
      audioBibles: PropTypes.arrayOf(
        PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired
      ).isRequired
    }),
    history: PropTypes.shape({
      push: PropTypes.func.isRequired
    }).isRequired
  };

  static defaultProps = {
    chapter: null,
    bible: null
  };

  isComponentMounted = false;

  constructor() {
    super();
    this.cordovaAudio = new CordovaAudio();
  }

  handleHideAudioController = () => this.setState({ isControllerOpen: false });

  handleShowAudioController = () => {
    if (!this.state.isPlayerEnabled) {
      this.setState({ isPlayerEnabled: true });
    }
    this.setState({ isControllerOpen: true });
  };

  handleSelectAudioBible = audioBibleId => {
    this.setState({
      selectedAudioBibleId: audioBibleId
    });
  };

  componentDidMount() {
    const { bible, chapter } = this.props;
    this.isComponentMounted = true;
    if (isCordovaApp) this.cordovaAudio.cordovaStopAudio();
    if (chapter && chapter.id && this.hasAudioBible(bible)) {
      const audioBibleId = bible.audioBibles[0].id;
      const chapterId = chapter.id;

      this.loadAudioChapter({
        audioBibleId,
        chapterId
      });
    }
  }

  shouldComponentUpdate = nextProps =>
    nextProps.bible !== null &&
    nextProps.chapter !== null &&
    nextProps.chapter.id !== null;

  componentDidUpdate = () => {
    const { bible, chapter } = this.props;
    const {
      audioChapters,
      currentChapterUid,
      isControllerOpen,
      isPlayable
    } = this.state;

    if (this.hasAudioBible(bible)) {
      const audioBibleId = bible.audioBibles[0].id;
      const chapterId = chapter.id;
      const chapterUid = [audioBibleId, chapterId].join(":");
      const nextChapter = audioChapters[chapterUid];

      if (currentChapterUid !== chapterUid) {
        if (!isControllerOpen) {
          // disable player and reset state if controller was not open
          this.setState({
            isPlayerEnabled: false
          });
        }

        this.setState({
          duration: 0,
          position: 0,
          isPlaying: false,
          isLoading: true, // defaults to true when not in cordova
          // isPlayPending: isCordovaApp, // defaults to true when is in cordova
          currentChapterUid: chapterUid
        });

        if (nextChapter) {
          if (isCordovaApp) this.initCordovaPlayer(nextChapter.resourceUrl);

          this.setState({ isPlayable: true });
        } else {
          this.loadAudioChapter({
            audioBibleId,
            chapterId
          });
        }
      }
    } else if (isPlayable) {
      this.setState({
        duration: 0,
        position: 0,
        isPlaying: false,
        isPlayerEnabled: false,
        isPlayable: false,
        currentChapterUid: null
      });
    }
  };

  componentWillUnmount = () => {
    this.isComponentMounted = false;
    if (isCordovaApp) this.cordovaAudio.cordovaStopAudio();
  };

  handlePlayPause = () => {
    if (!this.state.isPlaying) {
      this.play();
    } else {
      this.pause();
    }
  };

  handleAudioSeek = position => {
    if (isCordovaApp) {
      this.cordovaAudio.cordovaAudioSeekTo(position);
      this.updateState();
    } else this.seekAudio(position);
  };

  handleAudioControllerToggle = () => {
    if (!this.state.isControllerOpen) {
      return this.handleShowAudioController();
    }
    return this.handleHideAudioController();
  };

  handleHideAudioController = () => this.setState({ isControllerOpen: false });

  handleShowAudioController = () => {
    if (!this.state.isPlayerEnabled) {
      this.setState({ isPlayerEnabled: true });
    }
    this.setState({ isControllerOpen: true });
  };

  hasAudioBible = bible =>
    bible && bible.audioBibles && bible.audioBibles.length > 0;

  isAudioElementPlaying = () => {
    if (isCordovaApp) {
      return this.cordovaAudio.cordovaAudioIsPlaying();
    }
    return (
      this.reactAudioPlayer !== undefined &&
      this.reactAudioPlayer !== null &&
      !this.reactAudioPlayer.audioEl.paused &&
      !this.reactAudioPlayer.audioEl.ended
    );
  };

  updateState = ({ isPlayable, isLoading, isPlayPending } = {}) => {
    const newState = {
      duration: 0,
      position: 0,
      isPlaying: this.isAudioElementPlaying()
    };

    if (isPlayable !== undefined) {
      newState.isPlayable = isPlayable;
    }

    if (isLoading !== undefined) {
      newState.isLoading = isLoading;
    }

    if (isPlayPending !== undefined) {
      newState.isPlayPending = isPlayPending;
    }

    if (isCordovaApp) {
      this.cordovaAudio
        .cordovaGetCurrentPosition()
        .then(updatedState => {
          const { verseId, verseIdElement } = this.getVerseFromAudioPosition(
            updatedState.position
          );
          this.setState({
            ...newState,
            ...updatedState,
            currentAudioVerseId: verseId,
            currentAudioVerseElement: verseIdElement
          });
        })
        .catch(err => {
          console.log("setting state in catch:", err);
          this.setState(newState);
        });
    } else {
      if (
        this.reactAudioPlayer !== undefined &&
        this.reactAudioPlayer !== null
      ) {
        newState.duration = this.reactAudioPlayer.audioEl.duration || 0;
        newState.position = this.reactAudioPlayer.audioEl.currentTime || 0;

        const { verseId, verseIdElement } = this.getVerseFromAudioPosition(
          newState.position
        );
        newState.currentAudioVerseId = verseId;
        newState.currentAudioVerseElement = verseIdElement;

        if (this.state.isPlayPending && newState.position !== 0) {
          newState.isPlayPending = false;
        }
      }
      this.setState(newState);
    }
  };

  getVerseFromAudioPosition(position) {
    let verseId;
    const currentAudioChapter = this.getCurrentAudioChapter();
    if (currentAudioChapter && currentAudioChapter.timecodes) {
      const { timecodes } = currentAudioChapter;
      const verseIdIndex = timecodes.findIndex(({ end }) => position < end);
      if (verseIdIndex > -1) verseId = timecodes[verseIdIndex].verseId;
    }
    return {
      verseId,
      verseIdElement: document.querySelector(
        `.verse-span[data-verse-id="${verseId}"]`
      )
    };
  }

  loadAudioChapter({ audioBibleId, chapterId }) {
    return fetchAudioChapter({
      bibleId: audioBibleId,
      chapterId
    })
      .then(audioChapter => {
        if (audioChapter && this.isComponentMounted) {
          const chapterUid = [audioBibleId, audioChapter.id].join(":");
          if (isCordovaApp) this.initCordovaPlayer(audioChapter.resourceUrl);
          this.setState({
            audioChapters: {
              ...this.state.audioChapters,
              [chapterUid]: audioChapter
            },
            isPlayable: true
          });
        } else {
          throw new Error("No chapter returned from audio fetch!");
        }
        return audioChapter;
      })
      .catch(error => {
        console.log("Failed fetching audio chapter:", error);
        if (this.isComponentMounted) {
          this.updateState({
            isPlayable: false,
            isLoading: false
          });
        }
      });
  }

  handleMetadataLoad = () => {
    // audio chapter URL has loaded, but audio is still loading
    // until onCanPlay is fired by the react audio component
    if (this.isComponentMounted) {
      this.updateState();
    }
  };

  handlePlay = () => {
    this.updateState();
  };

  handleCanPlay = () => {
    // if (this.props.isPlaying) {
    //   this.reactAudioPlayer.audioEl.play();
    // }
    // this.props.onCanPlay();
    this.updateState({
      isLoading: false
    });
  };

  handleListen = () => {
    this.updateState();
  };

  handleEnded = () => {
    const { chapter, history, bible } = this.props;
    const { isPlaying } = this.state;
    if (
      (chapter.next && isPlaying) ||
      (isCordovaApp && this.cordovaAudio.cordovaAudioIsAutoPlay())
    ) {
      history.push(`/bible/${bible.id}/${chapter.next.id}`);
    } else {
      if (isCordovaApp) this.cordovaAudio.cordovaStopAudio();
      else this.reactAudioPlayer.audioEl.currentTime = 0;
      this.updateState();
    }
  };

  play = () => {
    if (isCordovaApp) {
      this.cordovaAudio.cordovaPlayAudio();
      this.cordovaAudio.cordovaSetTimer(this.updateState, 200);
    } else {
      if (this.state.position === 0) {
        this.setState({ isPlayPending: true });
      }

      if (this.reactAudioPlayer) {
        // onPlay will trigger updateState()`
        this.reactAudioPlayer.audioEl.play();
      }
    }
  };

  pause = () => {
    if (this.isAudioElementPlaying()) {
      // this.setState({ shouldAutoPlay: false });
      if (isCordovaApp) {
        this.cordovaAudio.cordovaPauseAudio();
      } else this.reactAudioPlayer.audioEl.pause();
      this.updateState();
    }
  };

  seekAudio = position => {
    if (this.reactAudioPlayer) {
      const audioElement = this.reactAudioPlayer.audioEl;
      // Is the player currently seeking?
      const isSeeking = audioElement.seeking;

      // Is the media seekable?
      const isSeekable =
        audioElement.seekable && audioElement.seekable.length > 0;

      if (!isSeeking && isSeekable) {
        audioElement.currentTime = position;
      }

      this.updateState();
    }
  };

  getCurrentAudioChapter = () => {
    const { chapter, bible } = this.props;
    const { audioChapters, selectedAudioBibleId } = this.state;

    if (chapter && this.hasAudioBible(bible)) {
      const audioBibleId = selectedAudioBibleId || bible.audioBibles[0].id;
      const chapterId = chapter.id;
      const chapterUid = [audioBibleId, chapterId].join(":");
      const currentAudioChapter = audioChapters[chapterUid];
      return currentAudioChapter;
    }
    return null;
  };

  initCordovaPlayer = src => {
    const { chapter, bible } = this.props;
    this.cordovaAudio.cordovaPrepAudio(
      src || "",
      this.updateState,
      this.handleEnded,
      {
        track: chapter.reference || "reference",
        artist: bible.nameLocal || "nameLocal"
      }
    );
    if (this.cordovaAudio.cordovaAudioIsAutoPlay()) {
      this.handlePlayPause();
    }
  };

  state = {
    position: 0,
    duration: 0,
    // shouldAutoPlay: true,
    isPlayerEnabled: true, // internal state used to determine if player should fetch and buffer
    isLoading: false, // fetch of audio chapter happening or audio buffering initially
    // currentChapterId: null,
    audioChapters: {},
    isPlayable: false, // an audio chapter is present
    isPlaying: false, // audio element is playing
    isPlayPending: false, // onPlayPause() has triggered a play, but audio element is not yet playing
    isControllerOpen: true,
    onToggleController: this.handleAudioControllerToggle,
    onHideController: this.handleHideAudioController,
    onShowController: this.handleShowAudioController,
    onSelectAudioBible: this.handleSelectAudioBible,
    onPlayPause: this.handlePlayPause,
    onAudioSeek: this.handleAudioSeek,
    hasAudioBible: this.hasAudioBible,
    getCurrentAudioChapter: this.getCurrentAudioChapter,
    selectedAudioBibleId: null
  };

  render = () => {
    const { bible, chapter, children } = this.props;
    const {
      audioChapters,
      isPlayerEnabled,
      isPlayable,
      selectedAudioBibleId
    } = this.state;
    const currentBible = bible;

    if (chapter && this.hasAudioBible(currentBible)) {
      const audioBibleId =
        selectedAudioBibleId || currentBible.audioBibles[0].id;
      const chapterId = chapter.id;
      const chapterUid = [audioBibleId, chapterId].join(":");
      const currentChapter = audioChapters[chapterUid];
      const now = new Date();

      if (
        isPlayerEnabled &&
        currentChapter !== undefined &&
        isPlayable &&
        currentChapter.expiresAt < now.getTime()
      ) {
        return (
          <ChapterAudioContext.Provider value={this.state}>
            {children}
            {!isCordovaApp && (
              <ReactAudioPlayer
                // autoPlay={this.state.shouldAutoPlay}
                prefetch="metadata"
                src={currentChapter.resourceUrl || ""}
                onLoadedMetadata={this.handleMetadataLoad}
                onCanPlay={this.handleCanPlay}
                onPlay={this.handlePlay}
                onListen={this.handleListen}
                onEnded={this.handleEnded}
                listenInterval={500}
                ref={element => {
                  this.reactAudioPlayer = element;
                }}
              />
            )}
          </ChapterAudioContext.Provider>
        );
      }
    }

    return (
      <ChapterAudioContext.Provider value={this.state}>
        {children}
      </ChapterAudioContext.Provider>
    );
  };
}

export default withRouter(ChapterAudio);

export function withAudio(WrappedComponent) {
  return function ComponentWithAudio(props) {
    return (
      <ChapterAudio.Consumer>
        {context => <WrappedComponent {...props} audio={context} />}
      </ChapterAudio.Consumer>
    );
  };
}
